import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';
import { getDefaultAvatar } from '../utils/avatar';

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { darkMode } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // User data state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    jobTitle: '',
    company: '',
    department: '',
    avatar: ''
  });

  const [originalData, setOriginalData] = useState({
    name: '',
    email: '',
    phone: '',
    jobTitle: '',
    company: '',
    department: '',
    avatar: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load user data from API
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await apiService.getCurrentUser();
        const data = {
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          jobTitle: userData.job_title || '',
          company: userData.company || '',
          department: userData.department || '',
          avatar: userData.avatar_url || getDefaultAvatar(userData.name, userData.id)
        };
        setProfileData(data);
        setOriginalData(data);
      } catch (error: any) {
        setError('Failed to load user data: ' + (error.message || 'Unknown error'));
      }
    };

    if (user) {
      loadUserData();
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const updateData: any = {
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone || null,
        job_title: profileData.jobTitle || null,
        company: profileData.company || null,
        department: profileData.department || null,
      };

      // If avatar is a base64 string, include it
      if (profileData.avatar && profileData.avatar.startsWith('data:')) {
        updateData.avatar_url = profileData.avatar;
      } else if (profileData.avatar) {
        updateData.avatar_url = profileData.avatar;
      }

      const updatedUser = await apiService.updateCurrentUser(updateData);
      
      // Update original data
      const newData = {
        name: updatedUser.name || '',
        email: updatedUser.email || '',
        phone: updatedUser.phone || '',
        jobTitle: updatedUser.job_title || '',
        company: updatedUser.company || '',
        department: updatedUser.department || '',
        avatar: updatedUser.avatar_url || getDefaultAvatar(updatedUser.name, updatedUser.id)
      };
      setOriginalData(newData);
      setProfileData(newData);
      
      // Refresh user in context
      await refreshUser();
      
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Password baru dan konfirmasi password tidak cocok');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('Password baru harus minimal 8 karakter');
      return;
    }

    setIsSaving(true);
    
    try {
      await apiService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      setShowChangePassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setSuccess('Password changed successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData(prev => ({
          ...prev,
          avatar: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Layout showSidebar={false} mainContentClass="flex-1" footerClass="">
      <div className={`${darkMode ? 'bg-gray-900' : 'bg-gray-50'} pt-14 pb-20 transition-colors duration-300`}>
        <div className="max-w-4xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-4">
            <Link 
              to="/dashboard" 
              className={`inline-flex items-center text-sm mb-3 transition-colors ${
                darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'
              }`}
            >
              <i className="fa-solid fa-arrow-left mr-2"></i>
              Kembali ke Dashboard
            </Link>
            <div className="flex items-center justify-between">
              <div>
                <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>My Profile</h1>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Kelola informasi profil dan pengaturan akun Anda</p>
              </div>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 flex items-center"
                >
                  <i className="fa-solid fa-edit mr-2"></i>
                  Edit Profile
                </button>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {/* Profile Picture & Basic Info */}
            <div className="lg:col-span-1">
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm p-4 transition-colors duration-300`}>
                <div className="text-center">
                  <div className="relative inline-block mb-3">
                    <img
                      src={profileData.avatar || getDefaultAvatar(profileData.name, user?.id)}
                      alt={profileData.name}
                      className={`w-24 h-24 rounded-full mx-auto border-4 ${darkMode ? 'border-violet-800' : 'border-violet-100'}`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = getDefaultAvatar(profileData.name, user?.id);
                      }}
                    />
                    {isEditing && (
                      <label className="absolute bottom-0 right-0 bg-violet-600 text-white p-2 rounded-full cursor-pointer hover:bg-violet-700 transition-colors">
                        <i className="fa-solid fa-camera text-sm"></i>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{profileData.name}</h2>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{profileData.jobTitle}</p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{profileData.company}</p>
                  <div className="mt-3">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      darkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-700'
                    }`}>
                      {profileData.department}
                    </span>
                  </div>
                </div>
              </div>

              {/* Account Info */}
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm p-4 mt-4 transition-colors duration-300`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Informasi Akun</h3>
                <div className="space-y-3">
                  <div>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Member Since</p>
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'long' }) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last Login</p>
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      {user?.last_login ? new Date(user.last_login).toLocaleString('id-ID') : 'Never'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</p>
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                      user?.is_active 
                        ? darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                        : darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                    }`}>
                      <i className={`fa-solid fa-circle text-xs mr-1`}></i>
                      {user?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Role</p>
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                      darkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-700'
                    }`}>
                      {user?.role || 'User'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Form */}
            <div className="lg:col-span-2">
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm p-6 transition-colors duration-300`}>
                {error && (
                  <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                    darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                  }`}>
                    <p className={`text-sm flex items-center ${
                      darkMode ? 'text-red-300' : 'text-red-700'
                    }`}>
                      <i className="fa-solid fa-exclamation-circle mr-2"></i>
                      {error}
                    </p>
                  </div>
                )}
                {success && (
                  <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                    darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                  }`}>
                    <p className={`text-sm flex items-center ${
                      darkMode ? 'text-green-300' : 'text-green-700'
                    }`}>
                      <i className="fa-solid fa-check-circle mr-2"></i>
                      {success}
                    </p>
                  </div>
                )}
                <form onSubmit={handleSave} className="space-y-4">
                  {/* Personal Information */}
                  <div>
                    <h3 className={`text-base font-semibold mb-3 flex items-center ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      <i className="fa-solid fa-user text-violet-600 mr-2"></i>
                      Informasi Pribadi
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Nama Lengkap <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={profileData.name}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          required
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm ${
                            darkMode
                              ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500 disabled:bg-gray-800 disabled:cursor-not-allowed'
                              : 'border-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed'
                          }`}
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Email <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <i className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} fa-solid fa-envelope`}></i>
                          </div>
                          <input
                            type="email"
                            name="email"
                            value={profileData.email}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            required
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500 disabled:bg-gray-800 disabled:cursor-not-allowed'
                                : 'border-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed'
                            }`}
                          />
                        </div>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Nomor Telepon
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <i className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} fa-solid fa-phone`}></i>
                          </div>
                          <input
                            type="tel"
                            name="phone"
                            value={profileData.phone}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500 disabled:bg-gray-800 disabled:cursor-not-allowed'
                                : 'border-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed'
                            }`}
                          />
                        </div>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Job Title
                        </label>
                        <input
                          type="text"
                          name="jobTitle"
                          value={profileData.jobTitle}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm ${
                            darkMode
                              ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500 disabled:bg-gray-800 disabled:cursor-not-allowed'
                              : 'border-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed'
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Company Information */}
                  <div>
                    <h3 className={`text-base font-semibold mb-3 flex items-center ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      <i className="fa-solid fa-building text-violet-600 mr-2"></i>
                      Informasi Perusahaan
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Company
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={profileData.company}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm ${
                            darkMode
                              ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500 disabled:bg-gray-800 disabled:cursor-not-allowed'
                              : 'border-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed'
                          }`}
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Department
                        </label>
                        <select
                          name="department"
                          value={profileData.department}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm ${
                            darkMode
                              ? 'bg-gray-900 border-gray-700 text-white disabled:bg-gray-800 disabled:cursor-not-allowed'
                              : 'border-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed'
                          }`}
                        >
                          <option value="Legal Team">Legal Team</option>
                          <option value="Business Team">Business Team</option>
                          <option value="Finance Team">Finance Team</option>
                          <option value="IT Team">IT Team</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {isEditing && (
                    <div className={`flex items-center justify-end space-x-3 pt-3 border-t mt-4 ${
                      darkMode ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditing(false);
                          setProfileData({ ...originalData });
                          setError(null);
                        }}
                        className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${
                          darkMode
                            ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Batal
                      </button>
                      <button
                        type="submit"
                        disabled={isSaving}
                        className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {isSaving ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                            Menyimpan...
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-save mr-2"></i>
                            Simpan Perubahan
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </form>

                {/* Change Password Section */}
                <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className={`text-base font-semibold flex items-center ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        <i className="fa-solid fa-lock text-violet-600 mr-2"></i>
                        Ubah Password
                      </h3>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Ganti password Anda untuk keamanan akun</p>
                    </div>
                    <button
                      onClick={() => setShowChangePassword(!showChangePassword)}
                      className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${
                        darkMode
                          ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {showChangePassword ? 'Batal' : 'Ubah Password'}
                    </button>
                  </div>

                  {showChangePassword && (
                    <form onSubmit={handleChangePassword} className={`space-y-3 border rounded-lg p-4 ${
                      darkMode 
                        ? 'bg-violet-900/20 border-violet-800' 
                        : 'bg-violet-50 border-violet-200'
                    }`}>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Password Saat Ini <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <i className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} fa-solid fa-lock`}></i>
                          </div>
                          <input
                            type="password"
                            name="currentPassword"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            required
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="Masukkan password saat ini"
                          />
                        </div>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Password Baru <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <i className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} fa-solid fa-lock`}></i>
                          </div>
                          <input
                            type="password"
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            required
                            minLength={8}
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="Minimal 8 karakter"
                          />
                        </div>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Konfirmasi Password Baru <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <i className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} fa-solid fa-lock`}></i>
                          </div>
                          <input
                            type="password"
                            name="confirmPassword"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            required
                            minLength={8}
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="Ulangi password baru"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-end">
                        <button
                          type="submit"
                          disabled={isSaving}
                          className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {isSaving ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                              Mengubah...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-key mr-2"></i>
                              Ubah Password
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;

